import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { combineLatest, map, Observable } from 'rxjs';
import { mockDataCategories, MockDataCategory, MockDataMode } from '../../../mocks/mock-data-config';
import { clearAdditionalComments, hasAdditionalComments } from '../../../mocks/data/comments/mocked-comments';
import { clearAdditionalOrganizations, clearStateChanges, hasAdditionalOrganizations, hasStateChanges, hasAdditionalMembers, clearAdditionalMembers } from '../../../mocks/data/organizations/mocked-organizations';

@Component({
  selector: 'app-mock-data-switcher',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './mock-data-switcher.component.html',
  styleUrls: ['./mock-data-switcher.component.scss']
})
export class MockDataSwitcherComponent {
  private router = inject(Router);

  categories: MockDataCategory[] = mockDataCategories;
  isExpanded = false;
  expandedCategory: string | null = null;
  clearConfirmationActive = false;
  clearOrganizationConfirmationActive = false;
  clearStateChangesConfirmationActive = false;
  clearMembersConfirmationActive = false;

  // Observable that combines all category mode streams
  readonly currentModes$: Observable<Map<string, string>> = combineLatest(
    this.categories.map(cat =>
      cat.currentMode$.pipe(
        map(mode => ({ id: cat.id, mode }))
      )
    )
  ).pipe(
    map(modes => new Map(modes.map(m => [m.id, m.mode])))
  );

  switchMode(category: MockDataCategory, mode: MockDataMode): void {
    console.log(`Switching ${category.label} mode to:`, mode.value);
    category.setMode(mode.value);
    this.isExpanded = false;
    this.expandedCategory = null;

    // Force complete reload by navigating to home and back
    const currentUrl = this.router.url;
    this.router.navigate(['/home']).then(() => {
      this.router.navigateByUrl(currentUrl);
    });
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
    if (!this.isExpanded) {
      this.expandedCategory = null;
    }
  }

  toggleCategory(categoryId: string): void {
    this.expandedCategory = this.expandedCategory === categoryId ? null : categoryId;
  }

  isCategoryExpanded(categoryId: string): boolean {
    return this.expandedCategory === categoryId;
  }

  getCurrentMode(categoryId: string): Observable<string> {
    return this.currentModes$.pipe(
      map(modes => modes.get(categoryId) || '')
    );
  }

  getCategoryLabel(category: MockDataCategory): string {
    return `${category.icon} ${category.label}`;
  }

  getCurrentModeLabel(category: MockDataCategory): Observable<string> {
    return this.getCurrentMode(category.id).pipe(
      map(currentMode => {
        const mode = category.modes.find(m => m.value === currentMode);
        return mode ? `${mode.icon} ${mode.label}` : '';
      })
    );
  }

  hasCustomComments(): boolean {
    return hasAdditionalComments();
  }

  initiateClearComments(event: Event): void {
    event.stopPropagation();
    this.clearConfirmationActive = true;
  }

  confirmClearComments(event: Event): void {
    event.stopPropagation();
    clearAdditionalComments();
    clearStateChanges();
    this.clearConfirmationActive = false;
    console.log('Custom comments and state changes cleared');

    // Force complete reload by navigating to home and back
    const currentUrl = this.router.url;
    this.router.navigate(['/home']).then(() => {
      this.router.navigateByUrl(currentUrl);
    });
  }

  cancelClearComments(event: Event): void {
    event.stopPropagation();
    this.clearConfirmationActive = false;
  }

  hasCustomOrganizations(): boolean {
    return hasAdditionalOrganizations();
  }

  initiateClearOrganizations(event: Event): void {
    event.stopPropagation();
    this.clearOrganizationConfirmationActive = true;
  }

  confirmClearOrganizations(event: Event): void {
    event.stopPropagation();
    clearAdditionalOrganizations();
    clearStateChanges();
    this.clearOrganizationConfirmationActive = false;
    console.log('Custom organizations and state changes cleared');

    // Force complete reload by navigating to home and back
    const currentUrl = this.router.url;
    this.router.navigate(['/home']).then(() => {
      this.router.navigateByUrl(currentUrl);
    });
  }

  cancelClearOrganizations(event: Event): void {
    event.stopPropagation();
    this.clearOrganizationConfirmationActive = false;
  }

  hasCustomStateChanges(): boolean {
    return hasStateChanges();
  }

  initiateClearStateChanges(event: Event): void {
    event.stopPropagation();
    this.clearStateChangesConfirmationActive = true;
  }

  confirmClearStateChanges(event: Event): void {
    event.stopPropagation();
    clearStateChanges();
    this.clearStateChangesConfirmationActive = false;
    console.log('State changes cleared');

    // Force complete reload by navigating to home and back
    const currentUrl = this.router.url;
    this.router.navigate(['/home']).then(() => {
      this.router.navigateByUrl(currentUrl);
    });
  }

  cancelClearStateChanges(event: Event): void {
    event.stopPropagation();
    this.clearStateChangesConfirmationActive = false;
  }

  hasCustomMembers(): boolean {
    return hasAdditionalMembers();
  }

  initiateClearMembers(event: Event): void {
    event.stopPropagation();
    this.clearMembersConfirmationActive = true;
  }

  confirmClearMembers(event: Event): void {
    event.stopPropagation();
    clearAdditionalMembers();
    this.clearMembersConfirmationActive = false;
    console.log('Custom members cleared');

    const currentUrl = this.router.url;
    this.router.navigate(['/home']).then(() => {
      this.router.navigateByUrl(currentUrl);
    });
  }

  cancelClearMembers(event: Event): void {
    event.stopPropagation();
    this.clearMembersConfirmationActive = false;
  }
}
